/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { ListingTable, type ListingColumn } from '@/shared/ui/components/basic/listing-table';
import {
  dueDayOfMonth,
  formatAccrualPeriod,
  formatCents,
  formatIsoDate,
  resolveSituation,
  sortExpectationsDesc,
  totalOpenCents,
  totalReceivedCents,
  type SituationTone,
} from '../utils/lease-detail-derivations';
import type { DueDatePolicy, LeaseExpectation } from '../types/lease.types';
import type { AccrualPeriodActions } from '../hooks/useAccrualPeriodActions';
import { LeaseAccrualPeriodDialog, dialogModeFor } from './LeaseAccrualPeriodDialog';

export interface LeaseAccrualPeriodsTableProps {
  expectations: LeaseExpectation[];
  /** The lease's due-date policy, used only for the footer note. */
  dueDatePolicy?: DueDatePolicy;
  /** The write side of each month; without it the table is read-only. */
  actions?: AccrualPeriodActions;
}

type AccrualPeriodRow = LeaseExpectation & { id: string };

/** Badge tones per situation, all resolved from tokens. */
const SITUATION_STYLE: Record<SituationTone, { bg: string; fg: string }> = {
  settled: { bg: 'var(--status-success-bg)', fg: 'var(--status-success-text)' },
  awaiting: { bg: 'var(--status-warning-bg)', fg: 'var(--status-warning-text)' },
  overdue: { bg: 'var(--status-error-bg)', fg: 'var(--status-error-text)' },
  open: {
    bg: 'var(--surface-tertiary)',
    fg: 'var(--text-secondary)',
  },
  writtenOff: {
    bg: 'var(--surface-tertiary)',
    fg: 'var(--text-tertiary)',
  },
  cancelled: {
    bg: 'var(--surface-tertiary)',
    fg: 'var(--text-tertiary)',
  },
};

/**
 * The rank a situation sorts on: severity/lifecycle order rather than the
 * badge label text, so an "overdue" row does not sort next to "open" by an
 * alphabetic accident. Overdue leads (needs attention), then awaiting, open,
 * settled, and finally the cancelled write-offs.
 */
const SITUATION_RANK: Record<SituationTone, number> = {
  overdue: 0,
  awaiting: 1,
  open: 2,
  settled: 3,
  writtenOff: 4,
  cancelled: 5,
};

/**
 * The competência panel: what was expected, what came in, and the derived
 * situation for each month [protótipo, Detalhe de Locação].
 *
 * Strictly read-only. The prototype offered "Registrar recebimento" and a
 * per-row "Confirmar", but the backend exposes no route for either -- the
 * rent-receipt service has no HTTP surface at all, and nothing transitions
 * an expectation out of `awaiting_confirmation` -- so both were left out
 * rather than wired to a 404.
 *
 * @param props.expectations - Rows straight from the competência panel API.
 * @returns The table card, or the designed empty state.
 */
export function LeaseAccrualPeriodsTable({
  expectations,
  dueDatePolicy,
  actions,
}: LeaseAccrualPeriodsTableProps) {
  const { t } = useTranslation('features/leases');
  const [openPeriod, setOpenPeriod] = useState<string | null>(null);

  if (expectations.length === 0) {
    return <AccrualPeriodsEmpty />;
  }

  const rows: AccrualPeriodRow[] = sortExpectationsDesc(expectations)
    .map((e) => ({ ...e, id: e.accrual_period }));

  const columns: ListingColumn<AccrualPeriodRow>[] = [
    {
      key: 'accrual_period',
      width: '168px',
      label: t('accrualPeriods.columns.accrual_period'),
      sortable: true,
      sortAccessor: (row) => row.accrual_period,
      // The due date rides under the month instead of in a column of its
      // own: both sort the same way, and the half-width card had no room
      // for the two once the situation and the action had what they need.
      render: (row) => (
        <span className="flex flex-col gap-(--space-0-5)">
          <Text
            component="span"
            className="text-(length:--text-body-sm) [font-weight:var(--font-medium)] text-(--text-primary)"
          >
            {formatAccrualPeriod(row.accrual_period)}
          </Text>
          {row.due_date && (
            <Text
              component="span"
              className="whitespace-nowrap text-(length:--text-meta) text-(--text-tertiary) [font-variant-numeric:tabular-nums]"
            >
              {t('detail.competence.due', { date: formatIsoDate(row.due_date) })}
            </Text>
          )}
        </span>
      ),
    },
    {
      key: 'expected',
      width: '120px',
      label: t('accrualPeriods.columns.expected'),
      align: 'right',
      sortable: true,
      sortType: 'number',
      sortAccessor: (row) => row.expected_cents ?? null,
      render: (row) => (
        <Text
          component="span"
          className="text-(length:--text-body-sm) text-(--text-primary) [font-variant-numeric:tabular-nums]"
        >
          {formatCents(row.expected_cents)}
        </Text>
      ),
    },
    {
      key: 'received',
      width: '120px',
      label: t('accrualPeriods.columns.received'),
      align: 'right',
      sortable: true,
      sortType: 'number',
      sortAccessor: (row) => row.received_cents ?? null,
      render: (row) => <ReceivedCell row={row} />,
    },
    {
      key: 'situation',
      label: t('accrualPeriods.columns.situation'),
      align: 'right',
      sortable: true,
      sortType: 'number',
      sortAccessor: (row) => SITUATION_RANK[resolveSituation(row).tone],
      render: (row) => <SituationBadge row={row} />,
    },
  ];
  if (actions) {
    columns.push({
      key: 'action',
      label: '',
      width: '176px',
      align: 'right',
      render: (row) => <RowAction row={row} onOpen={() => setOpenPeriod(row.accrual_period)} />,
    });
  }
  const opened = rows.find((row) => row.accrual_period === openPeriod) ?? null;

  return (
    <div className="flex flex-col overflow-hidden rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-card)">
      {/*
        * The table is fixed-layout, which splits the width evenly unless a
        * column says otherwise. Equal columns in the half-width card clipped
        * "Registrar pagamento" and ran the situation pill into the next
        * cell. The fixed columns get what they need, the situation takes
        * the rest, and below the minimum the card scrolls instead.
        */}
      <ListingTable
        bare
        minWidth={actions ? '760px' : '580px'}
        data={rows}
        columns={columns}
        testId="lease-accrualPeriods-table"
        rowTestId="lease-accrualPeriod-row"
        /*
         * The tint goes through `getRowStyle`, not `getRowClassName`.
         * ListingTable writes an inline `background` on every row, which no
         * class can outrank — the overdue tint was there in the markup and
         * never rendered.
         */
        getRowStyle={(row) => (
          resolveSituation(row).tone === 'overdue'
            // A thin error rule on the leading edge, not a filled row: the
            // pill already says "em atraso", and a full tint stacked on it
            // turned every late month into a pink band.
            ? { boxShadow: 'inset 3px 0 0 var(--status-error-border)' }
            : undefined
        )}
        /* The marker is what a test can see: happy-dom drops a `background`
         * carrying `color-mix`, so the tint itself is only observable in a
         * real browser. */
        getRowProps={(row): Record<string, string> => (
          resolveSituation(row).tone === 'overdue' ? { 'data-overdue': 'true' } : {}
        )}
      />
      <AccrualPeriodsFooter expectations={expectations} dueDatePolicy={dueDatePolicy} />
      {actions && (
        <LeaseAccrualPeriodDialog
          expectation={opened}
          actions={actions}
          onClose={() => setOpenPeriod(null)}
        />
      )}
    </div>
  );
}

/**
 * The one move a month invites, named for what the broker does there:
 * confirm a pro-rata, record a payment, or look back at a closed month.
 */
function RowAction({ row, onOpen }: { row: AccrualPeriodRow; onOpen: () => void }) {
  const { t } = useTranslation('features/leases');
  const mode = dialogModeFor(row);

  return (
    <UnstyledButton
      type="button"
      onClick={onOpen}
      data-testid={`lease-accrual-action-${row.accrual_period}`}
      className={mode === 'history'
        ? 'whitespace-nowrap text-(length:--text-meta) text-(--text-secondary) hover:text-(--seed-base) hover:underline'
        : 'whitespace-nowrap rounded-(--radius-md) border border-(--border-subtle) px-(--space-2-5) py-(--space-1) text-(length:--text-meta) [font-weight:var(--font-semibold)] text-(--seed-base) hover:border-(--seed-base) hover:bg-(--surface-hover)'}
    >
      {t(`accrualPeriods.rowAction.${mode}`)}
    </UnstyledButton>
  );
}

/**
 * Received amount: an em-dash while nothing has come in, tinted once it has.
 */
/**
 * What actually arrived. A baixa is stored as a receipt for the forgiven
 * amount, so `received_cents` counts it; a month written off would read
 * "Recebido R$ 3.700,00" for money nobody paid. The receipts list, when the
 * API sends it, separates the two.
 */
function paidCents(row: AccrualPeriodRow): number {
  if (!row.receipts) return row.received_cents;
  return row.receipts
    .filter((receipt) => receipt.kind === 'payment')
    .reduce((sum, receipt) => sum + receipt.amount_cents, 0);
}

/** The amount actually paid for the month, or a dash. */
function ReceivedCell({ row }: { row: AccrualPeriodRow }) {
  const paid = paidCents(row);
  const hasReceipt = paid > 0;

  return (
    <Text
      component="span"
      className="text-(length:--text-body-sm) [font-weight:var(--font-medium)] [font-variant-numeric:tabular-nums]"
      style={{ color: hasReceipt ? 'var(--status-success-text)' : 'var(--text-tertiary)' }}
    >
      {hasReceipt ? formatCents(paid) : '—'}
    </Text>
  );
}

/**
 * The derived situation as a pill: baixado, aguardando confirmação, N dias
 * em atraso, em dia, or baixa cancelada.
 */
function SituationBadge({ row }: { row: AccrualPeriodRow }) {
  const { t } = useTranslation('features/leases');
  const { tone, daysOverdue } = resolveSituation(row);
  const style = SITUATION_STYLE[tone];
  const label = tone === 'overdue'
    ? t('accrualPeriods.situation.overdue', { count: daysOverdue })
    : t(`accrualPeriods.situation.${tone}`);

  return (
    <Text
      component="span"
      data-testid={`lease-accrualPeriod-situation-${tone}`}
      className="inline-flex items-center rounded-full px-(--space-2-5) py-(--space-1) text-(length:--text-meta) [font-weight:var(--font-medium)] whitespace-nowrap"
      style={{ background: style.bg, color: style.fg }}
    >
      {label}
    </Text>
  );
}

/**
 * The card footer: how competências are raised, and the running totals.
 */
function AccrualPeriodsFooter({
  expectations,
  dueDatePolicy,
}: {
  expectations: LeaseExpectation[];
  dueDatePolicy?: DueDatePolicy;
}) {
  const { t } = useTranslation('features/leases');
  const open = totalOpenCents(expectations);
  const received = totalReceivedCents(expectations);
  const dueDay = dueDayOfMonth(dueDatePolicy);

  return (
    <div className="flex flex-col gap-(--space-1) border-t border-(--border-subtle) px-(--space-4) py-(--space-3)">
      {dueDay !== null && (
        <Text component="span" className="text-(length:--text-meta) text-(--text-tertiary)">
          {t('accrualPeriods.footer.generatedOn', { day: dueDay })}
        </Text>
      )}
      <div className="flex items-center justify-between gap-(--space-3) flex-wrap">
        <Text component="span" className="text-(length:--text-meta) text-(--text-tertiary)">
          {t('accrualPeriods.footer.received', { amount: formatCents(received) })}
        </Text>
        <Text
          component="span"
          className="text-(length:--text-body-sm) [font-weight:var(--font-semibold)] text-(--text-primary)"
        >
          {t('accrualPeriods.footer.open', { amount: formatCents(open) })}
        </Text>
      </div>
      <Text component="span" className="text-(length:--text-micro) text-(--text-tertiary)">
        {t('accrualPeriods.footer.awaitingNote')}
      </Text>
    </div>
  );
}

/**
 * The designed empty state, for a lease whose competências have not been
 * generated yet -- a draft lease, most often.
 */
function AccrualPeriodsEmpty() {
  const { t } = useTranslation('features/leases');

  return (
    <div
      data-testid="lease-accrualPeriods-empty"
      className="flex flex-col items-center gap-(--space-2) rounded-(--radius-lg) border border-dashed border-(--border-medium) bg-(--surface-primary) px-(--space-5) py-(--space-6) text-center"
    >
      <Text
        component="span"
        className="text-(length:--text-body-sm) [font-weight:var(--font-semibold)] text-(--text-primary)"
      >
        {t('accrualPeriods.emptyTitle')}
      </Text>
      <Text component="span" className="text-(length:--text-meta) text-(--text-secondary) max-w-[42ch]">
        {t('accrualPeriods.emptyDescription')}
      </Text>
    </div>
  );
}

export default LeaseAccrualPeriodsTable;
